import React, { FC } from 'react';

import { DragDropProvider } from '@dnd-kit/react';
import { isSortable, useSortable } from '@dnd-kit/react/sortable';

import {
	BUTTON_TYPES,
	DragIcon,
	EditIcon,
	EvButton,
	EvConfirm,
	EvIcon,
	RemoveIcon
} from '@evinced-private/ui-common';

import { defaultGroupingRuleUrl } from '../../../../consts/defaultGroupingRuleUrl';
import { SITE_SCANNER_APP_ID } from '../../../../consts/dom-consts';
import { queryParamsOptions } from '../../../../consts/queryParamsOptions';
import { IGroupingRule } from '../../../../interfaces/GroupingRule';

interface IGroupingRulesListProps {
	groupingRules?: IGroupingRule[];
	onRemoveRule: (url: string) => void;
	onReorder: (fromIndex: number, toIndex: number) => void;
	onEditRule: (rule: IGroupingRule) => void;
}

interface ISortableRuleProps {
	rule: IGroupingRule;
	index: number;
	onRemoveRule: (url: string) => void;
	onEditRule: (rule: IGroupingRule) => void;
}

const SortableRule: FC<ISortableRuleProps> = ({ rule, index, onRemoveRule, onEditRule }) => {
	const { ref, handleRef, isDragSource } = useSortable({
		id: rule.urlPattern,
		index
	});

	if (rule.urlPattern === defaultGroupingRuleUrl) {
		return null;
	}

	let queryParams = queryParamsOptions.INCLUDE_ALL;
	if (rule.ignoreQueryParameters) {
		queryParams =
			rule.excludedQueryParameters && rule.excludedQueryParameters.length > 0
				? queryParamsOptions.LIST
				: queryParamsOptions.IGNORE_ALL;
	}

	return (
		<div ref={ref} style={{ opacity: isDragSource ? 0.5 : 1 }}>
			<div className="rule-line">
				<div className="rule-field handle" ref={handleRef} aria-label="drag handle">
					<EvIcon icon={DragIcon} small />
				</div>
				<div className="rule-info">
					<div className="rule-field">
						<div className="prop-name">URL:</div>
						<div className="prop-value urlpattern-value" title={rule.urlPattern}>
							{rule.urlPattern}
						</div>
					</div>
					<div className="rule-field">
						<div className="prop-name">Sampling:</div>
						<div className="prop-value" title={`${rule.maxSamplesCount}` || 'Unlimited'}>
							{rule.maxSamplesCount || 'Unlimited'}
						</div>
					</div>
					<div className="rule-field">
						<div className="prop-name long-name">Query parameters:</div>
						<div className="prop-value" title={queryParams}>
							{queryParams}
						</div>
					</div>
					<div className="rule-field">
						<div className="prop-name">Attributes:</div>
						<div
							className="prop-value"
							title={rule.ignoreFragment && rule.caseSensitive ? 'default' : 'custom'}
						>
							{rule.ignoreFragment && rule.caseSensitive ? 'default' : 'custom'}
						</div>
					</div>
				</div>
				<div className="rule-actions">
					<EvButton
						type={BUTTON_TYPES.ICON}
						ariaLabel={`Edit grouping rule ${index}`}
						className="rule-action"
						onClick={() => {
							onEditRule(rule);
						}}
					>
						<EvIcon icon={EditIcon} />
					</EvButton>
					<EvConfirm
						appElement={SITE_SCANNER_APP_ID}
						title="Delete rule"
						triggerButtonProps={{
							type: BUTTON_TYPES.ICON,
							title: 'Delete rule',
							children: <EvIcon icon={RemoveIcon} />
						}}
						approveButtonText="Delete"
						promptMessage="Are you sure you want to delete this rule?"
						onConfirm={(closeModal) => {
							onRemoveRule(rule.urlPattern);
							closeModal();
						}}
					/>
				</div>
			</div>
		</div>
	);
};

export const GroupingRulesList: FC<IGroupingRulesListProps> = ({
	groupingRules,
	onRemoveRule,
	onEditRule,
	onReorder
}: IGroupingRulesListProps) => {
	return (
		<DragDropProvider
			onDragEnd={(event) => {
				if (event.canceled) {
					return;
				}

				const { source } = event.operation;
				if (!isSortable(source)) {
					return;
				}

				const { initialIndex, index } = source.sortable;
				if (initialIndex !== index) {
					onReorder(initialIndex, index);
				}
			}}
		>
			<div>
				{groupingRules.map((rule: IGroupingRule, index: number) => (
					<SortableRule
						key={rule.urlPattern}
						rule={rule}
						index={index}
						onRemoveRule={onRemoveRule}
						onEditRule={onEditRule}
					/>
				))}
			</div>
		</DragDropProvider>
	);
};
